import { useEffect, useState } from 'react';
import { useAddItemsToCart, useCart } from 'hooks';
import classNames from 'classnames';
import { useSettings } from 'contexts/SettingContext';
import useProductAddToCartTracking from 'hooks/tracking/useProductAddToCartTracking';
import { useRouter } from 'next/router';
import useProductAddedTracking from 'hooks/tracking/useProductAddedTracking';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import { getSelectedSalonNo, getStorageCustomerSalonNo, getCurrencyCode } from 'lib/shopify';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useGlobalModules } from 'contexts';
import Select from 'components/shared/select';
import BackInStockNotifier from '../backInStockNotifier';
import styles from './cartSection.module.scss';

const CartSection = ({
  className,
  shopifyData,
  productData,
  locale,
  sellingPlan,
  addonSelections,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const router = useRouter();
  const addItems = useAddItemsToCart();
  const { addProduct, trackedItemsSelectList } = useSettings();
  const globalModules = useGlobalModules();
  const salonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);

  // This is to initialize the cart when the customer is navigating to a product page
  const { cart } = useCart();
  const { linkToAnotherProductMessage, linkToAnotherProductButtonText } =
    globalModules.translations.product;
  const { addToCart, addingToCart } = globalModules.translations.button;
  const {
    content: oosContent,
    success: oosSuccess,
    submit: oosSubmit,
  } = globalModules.translations.oos;
  const { linkToAnotherProductBackground, linkToAnotherProductText } = globalModules.colors;
  const outOfStock = shopifyData?.outOfStock;

  const selectOptions = [];
  for (let i = 1; i <= 10; i += 1) {
    selectOptions.push({ text: `${i}`, value: i });
  }

  const handleAddToCart = async () => {
    setIsLoading(true);
    const productSelectListObj = trackedItemsSelectList?.find(
      p => p?.productId === String(shopifyData?.productId)
    );

    const keuneRepeat = (sellingPlan?.options?.[0]?.value?.split(' ') || [])[0] || 0;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useProductAddToCartTracking({
      product: {
        quantity,
        name: shopifyData?.productTitle,
        id: shopifyData?.productId,
        productSpecsCollection: productData?.productSpecsCollection?.items,
        price: shopifyData?.productPrice,
        itemListName: productSelectListObj?.itemListName,
        currencyCode: getCurrencyCode(locale),
        keune_repeat: parseInt(keuneRepeat, 10),
        image_url: productData?.thumbnailImage?.url,
        compare_at_price: shopifyData?.productCompareAtPrice || null,
      },
    });

    // Universal Analytics
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useProductAddedTracking({
      affiliatedSalonNo: salonNo,
      selectedSalonNo,
      product: {
        name: shopifyData?.productTitle,
        id: shopifyData?.productId,
        brand: 'Keune',
        category: productData?.productSpecsCollection?.items?.find(
          ({ type }) =>
            type.filterType === 'Producttype' ||
            type.filterType === 'Type de produit' ||
            type.filterType === 'Product type'
        )?.value,
        price: shopifyData?.productPrice,
        discount: shopifyData?.productCompareAtPrice
          ? shopifyData?.productCompareAtPrice - shopifyData?.productPrice
          : null,
        quantity,
      },
      location: { action: router },
    });

    const relatedProducts = shopifyData.relatedProducts || [];

    const productsDataForCheckout = [
      {
        variantId: shopifyData.variantId,
        quantity,
        sellingPlanId: sellingPlan?.id,
      },
      ...relatedProducts.map(id => ({ variantId: id, quantity })),
      ...Object.entries(addonSelections || {})
        .filter(([, checked]) => checked)
        .map(([variantId]) => ({ variantId, quantity: 1 })),
    ];

    await addItems(productsDataForCheckout);

    setIsLoading(false);
  };

  useEffect(() => {
    setQuantity(1);
  }, [router.asPath]);

  if (shopifyData?.isFreeItem) return null;

  if (productData?.linkToAnotherProduct?.slug && productData?.useLinkToAnotherProduct !== false) {
    return (
      <div className={classNames(className, styles.linkToAnotherProduct)}>
        <p>{linkToAnotherProductMessage}</p>
        <SiteLink
          locale={locale}
          className={styles.linkToAnotherButton}
          style={{
            backgroundColor: linkToAnotherProductBackground,
            color: linkToAnotherProductText,
          }}
          href={addLocaleToHref(productData?.linkToAnotherProduct?.slug, locale, router)}
        >
          {linkToAnotherProductButtonText}
        </SiteLink>
      </div>
    );
  }
  return (
    <div className={classNames(className, styles.cartSection)}>
      {outOfStock ? (
        <BackInStockNotifier
          locale={locale}
          text={oosContent.json}
          successText={oosSuccess}
          submitText={oosSubmit}
          productIds={[shopifyData?.variantId]}
        />
      ) : (
        <div className={styles.controls}>
          <Select
            className={styles.select}
            options={selectOptions}
            selected={quantity || selectOptions[0].value}
            onChange={value => setQuantity(parseInt(value, 10))}
            ariaLabel={globalModules?.translations?.accessibility?.quantity || 'Quantity'}
          />
          <button
            type="button"
            disabled={outOfStock}
            className={styles.addToCartButton}
            aria-label={productData?.title ? `${addToCart}: ${productData.title}` : undefined}
            onClick={() => {
              handleAddToCart();
              addProduct({
                title: productData.title,
                slug: productData.slug,
                variantId: getShopifyGid('ProductVariant', shopifyData?.variantId),
              });
            }}
          >
            {isLoading ? addingToCart : addToCart}
          </button>
        </div>
      )}
    </div>
  );
};

export default CartSection;
